import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createStelle, updateStelle, deleteStelle, feedbackSenden, gcalStatus, msCalStatus, extractStellenPdf } from './api';
import MobileStelleFlow from './components/MobileStelleFlow';
import MobileSektionEditor from './components/MobileSektionEditor';
import MobileAnalytics from './MobileAnalytics';
import MobileProfil from './MobileProfil';
import MobileEinstellungen from './MobileEinstellungen';
import AdminPanel from './components/AdminPanel';
import './MobileStudio.css';

import { THEMES, T, istEntwurf } from './utils/themeUtils';
import { CheckIcon } from './components/MobileIcons';
import { ProfilVollstaendigkeitKarte, BewerbungsrateCard, KPIRow, DashboardJobCard } from './components/MobileCards';
import NavIsland from './components/MobileNavIsland';
import AddJobModal from './components/MobileAddJobModal';
import MobileSucheOverlay from './components/MobileSucheOverlay';
import MobileBottomSheet from './components/MobileBottomSheet';
import MobileMeineDokumente from './MobileMeineDokumente';

// ─── Main Component ───────────────────────────────────────
function MobileStudio({ nutzer, stellen, setStellen, aktiveStelle, onAuswahl, onGeaendert, onProfilOeffnen, hatProfile, profilLaedt, aktivesProfile, onKeinKontingent, rolle, onBasisBearbeiten, onAbmelden, darkMode, toggleDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();

  // URL → nav-id mapping (gleiche Logik wie Studio.js)
  const URL_TO_NAV = {
    '/analytics':       'analytics',
    '/suche':           'suche',
    '/meine-dokumente': 'dokumente',
    '/admin':           'admin',
  };
  const NAV_TO_URL = {
    dashboard: '/dashboard',
    analytics: '/analytics',
    suche:     '/suche',
    dokumente: '/meine-dokumente',
    admin:     '/admin',
  };
  const navFromUrl = URL_TO_NAV[location.pathname] || 'dashboard';

  // Overlay-Views ohne eigene URL (profil, einstellungen)
  const [localView, setLocalView] = useState(null);

  // Effektiver aktiver Nav
  const activeNav = localView || navFromUrl;

  // URL-Wechsel via Browser-Back/Forward: Overlay schließen
  useEffect(() => { setLocalView(null); }, [location.pathname]);

  const [ansicht, setAnsicht] = useState('main');
  const [modal, setModal] = useState(null);
  const oeffneModal = () => setModal({ tab: 'link', prefill: '' });
  const [laedt, setLaedt] = useState(false);
  const [pdfLaedt, setPdfLaedt] = useState(false);
  const [toast, setToast] = useState(null);
  const [themeId, setThemeId] = useState(() => localStorage.getItem('nav_theme') || 'paper');
  const th = THEMES[themeId] || THEMES.paper;

  const onThemeChange = (id) => {
    setThemeId(id);
    localStorage.setItem('nav_theme', id);
  };
  const [autoStart, setAutoStart] = useState(false);
  const [tonlage, setTonlage] = useState(() => localStorage.getItem('tonlage') || 'klassisch');
  const [keinProfilWarnung, setKeinProfilWarnung] = useState(false);
  const [feedbackModalOffen, setFeedbackModalOffen] = useState(false);
  const [feedbackBetreff, setFeedbackBetreff] = useState('');
  const [feedbackNachricht, setFeedbackNachricht] = useState('');
  const [feedbackBilder, setFeedbackBilder] = useState([]);
  const [feedbackLaedt, setFeedbackLaedt] = useState(false);
  const [feedbackGesendet, setFeedbackGesendet] = useState(false);
  const [feedbackFehler, setFeedbackFehler] = useState('');
  const markierteIds = useMemo(() => new Set(stellen.filter(s => s.gepinnt).map(s => s.id)), [stellen]);

  const [navOffen, setNavOffen] = useState(false);
  const [gcalVerbunden, setGcalVerbunden] = useState(false);
  const [msCalVerbunden, setMsCalVerbunden] = useState(false);
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [calendarEintraege, setCalendarEintraege] = useState(() => {
    try { return JSON.parse(localStorage.getItem('calendar_eintraege') || '[]'); }
    catch { return []; }
  });
  const [profilMenuOffen, setProfilMenuOffen] = useState(false);
  const profilMenuRef = useRef(null);
  const [editSektion, setEditSektion] = useState(null);
  const [editSektionRender, setEditSektionRender] = useState(null);

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const swipeDirectionLock = useRef(null); // 'h' | 'v' | null

  useEffect(() => {
    if (!profilMenuOffen) return;
    const handler = (e) => {
      if (profilMenuRef.current && !profilMenuRef.current.contains(e.target)) {
        setProfilMenuOffen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [profilMenuOffen]);

  const initialen = nutzer?.displayName
    ? nutzer.displayName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';


  useEffect(() => {
    gcalStatus().then(r => setGcalVerbunden(r.data.verbunden)).catch(() => {});
    msCalStatus().then(r => setMsCalVerbunden(r.data.verbunden)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gcal') === 'verbunden') {
      setGcalVerbunden(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('mscal') === 'verbunden') {
      setMsCalVerbunden(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const onMarkierenToggle = (id) => {
    const gepinnt = !stellen.find(s => s.id === id)?.gepinnt;
    setStellen(prev => prev.map(s => s.id === id ? { ...s, gepinnt } : s));
    updateStelle(id, { gepinnt }).catch(() => {
      setStellen(prev => prev.map(s => s.id === id ? { ...s, gepinnt: !gepinnt } : s));
    });
  };

  const [duplikatWarnung, setDuplikatWarnung] = useState(null); // null | inputData

  const erstelleStelleWirklich = async (inputData) => {
    setDuplikatWarnung(null);
    setLaedt(true);
    try {
      // Offene Stellen (ohne generierte Dokumente) automatisch entfernen
      const offeneStellen = stellen.filter(s => istEntwurf(s) && !s.url && !s.text);
      for (const offene of offeneStellen) {
        try {
          await deleteStelle(offene.id);
          onGeaendert(offene, 'loeschen');
        } catch (e) {
          console.error('[Studio] Offene Stelle konnte nicht gelöscht werden:', e);
        }
      }

      const payload = { name: 'Neue Stellenanzeige' };
      if (inputData.url) payload.url = inputData.url;
      if (inputData.text) payload.text = inputData.text;

      const res = await createStelle(payload);
      onGeaendert(res.data, 'neu');
      setModal(false);

      onAuswahl(res.data);
      setAutoStart(true);
      setAnsicht('flow');
    } finally {
      setLaedt(false);
    }
  };

  const onNeu = async (inputData = {}) => {
    if (profilLaedt || !hatProfile) {
      setModal(null);
      setKeinProfilWarnung(true);
      return;
    }

    // PDF-Extract Pfad
    if (inputData.type === 'pdf' && inputData.pdfFile) {
      setPdfLaedt(true);
      try {
        const res = await extractStellenPdf(inputData.pdfFile);
        const extractedText = res.data.text || '';
        const textInputData = { type: 'text', url: '', text: extractedText };

        const isDuplicate = stellen.some(s =>
          s.text && s.text.trim().slice(0, 300) === extractedText.trim().slice(0, 300)
        );
        if (isDuplicate) {
          setModal(false);
          setDuplikatWarnung(textInputData);
          return;
        }
        await erstelleStelleWirklich(textInputData);
      } catch (err) {
        console.error('[Mobile] PDF-Extraktion fehlgeschlagen:', err);
        setToast({ msg: 'PDF konnte nicht gelesen werden. Bitte Text-Tab verwenden.', type: 'error' });
        setTimeout(() => setToast(null), 3500);
      } finally {
        setPdfLaedt(false);
      }
      return;
    }

    const isDuplicate = stellen.some(s => {
      if (inputData.url && s.url) return s.url.trim() === inputData.url.trim();
      if (inputData.text && s.text) return s.text.trim().slice(0, 300) === inputData.text.trim().slice(0, 300);
      return false;
    });

    if (isDuplicate) {
      setModal(false);
      setDuplikatWarnung(inputData);
      return;
    }

    await erstelleStelleWirklich(inputData);
  };

  const onStelleAuswahl = (s) => {
    onAuswahl(s);
    setAutoStart(false);
    setAnsicht('flow');
  };

  const onStelleLoeschen = async (stelle) => {
    try {
      await deleteStelle(stelle.id);
      onGeaendert(stelle, 'loeschen');
    } catch (err) {
      console.error('Löschen fehlgeschlagen', err);
    }
  };

  const onStelleUmbenennen = async (stelle, neuerName) => {
    try {
      await updateStelle(stelle.id, { name: neuerName });
      onGeaendert({ ...stelle, name: neuerName }, 'update');
    } catch (err) {
      console.error('Umbenennen fehlgeschlagen', err);
    }
  };

  const onStelleAbschicken = async (stelle) => {
    try {
      await updateStelle(stelle.id, { bewerbungs_status: 'abgeschickt' });
      onGeaendert({ ...stelle, bewerbungs_status: 'abgeschickt' }, 'update');
    } catch (err) {
      console.error('Abschicken fehlgeschlagen', err);
    }
  };

  const saveStelleField = async (stelleId, updates) => {
    const stelle = stellen.find(s => s.id === stelleId);
    if (!stelle) return;
    try {
      await updateStelle(stelleId, updates);
      onGeaendert({ ...stelle, ...updates }, 'update');
    } catch (err) {
      console.error('Speichern fehlgeschlagen', err);
    }
  };

  const onStelleBearbeiten = (s) => {
    onAuswahl(s);
    setAutoStart(false);
    setAnsicht('flow');
  };

  const onNavSelect = (id) => {
    if (NAV_TO_URL[id]) {
      setLocalView(null);
      navigate(NAV_TO_URL[id]);
    } else {
      setLocalView(id);
    }
    setNavOffen(false);
  };

  const onStelleVomNav = (s) => {
    onAuswahl(s);
    setAutoStart(false);
    setAnsicht('flow');
    setNavOffen(false);
  };

  // ── Flow-mode section editor ──────────────────────────

  const onSektionBearbeiten = (sektionIdx, sektion) => {
    setEditSektionRender({ sektion, sektionIdx });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setEditSektion({ sektion, sektionIdx });
    }));
  };

  const onSektionEditorSchliessen = () => {
    setEditSektion(null);
    setTimeout(() => setEditSektionRender(null), 380);
  };

  const onSektionSave = async (neueSektion) => {
    if (!aktiveStelle || !editSektionRender) return;
    const { sektionIdx } = editSektionRender;

    let updates = {};
    if (aktiveStelle.cv_data) {
      const neueSektionen = [...aktiveStelle.cv_data.sektionen];
      neueSektionen[sektionIdx] = neueSektion;
      updates = { cv_data: { ...aktiveStelle.cv_data, sektionen: neueSektionen } };
    } else if (aktiveStelle.cv_rohtext) {
      const { sektionen } = parseCvRohtext(aktiveStelle.cv_rohtext);
      const neueSektionen = sektionen.map((s, i) => i === sektionIdx ? neueSektion : s);
      const header = aktiveStelle.cv_rohtext.split('\n## ')[0].trim();
      const body = neueSektionen.map(s => `## ${s.name}\n${s.inhaltRaw}`).join('\n');
      updates = { cv_rohtext: header ? `${header}\n${body}` : body };
    } else {
      return;
    }

    try {
      await updateStelle(aktiveStelle.id, updates);
      onGeaendert({ ...aktiveStelle, ...updates }, 'update');
    } catch (e) {
      console.error('Sektion speichern fehlgeschlagen', e);
    }
    onSektionEditorSchliessen();
  };

  // ── Swipe gestures ────────────────────────────────────
  // Direction is locked early in onTouchMove (after 10px movement).
  // Vertical lock cancels any horizontal nav trigger.
  // Requires 2.5:1 horizontal-to-vertical ratio to fire.

  const onSwipeTouchStart = (e) => {
    if (e.target.closest('[data-swipe-internal]')) { touchStartX.current = null; return; }
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    swipeDirectionLock.current = null;
  };

  const onSwipeTouchMove = (e) => {
    if (touchStartX.current === null || swipeDirectionLock.current) return;
    const dx = Math.abs(e.touches[0].clientX - touchStartX.current);
    const dy = Math.abs(e.touches[0].clientY - touchStartY.current);
    if (dx < 10 && dy < 10) return; // not enough movement yet
    swipeDirectionLock.current = dx > dy ? 'h' : 'v';
  };

  const onMainTouchEnd = (e) => {
    if (touchStartX.current === null || swipeDirectionLock.current !== 'h') {
      touchStartX.current = null; swipeDirectionLock.current = null; return;
    }
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null; swipeDirectionLock.current = null;

    if (Math.abs(deltaX) < 60 || Math.abs(deltaX) < Math.abs(deltaY) * 2.5) return;

    if (deltaX > 0) setNavOffen(true);
    else if (deltaX < 0 && navOffen) setNavOffen(false);
  };

  const onFlowTouchEnd = (e) => {
    if (touchStartX.current === null || swipeDirectionLock.current !== 'h') {
      touchStartX.current = null; swipeDirectionLock.current = null; return;
    }
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null; swipeDirectionLock.current = null;

    // Block nav while bottom sheet is open
    if (editSektion) return;
    if (Math.abs(deltaX) < 60 || Math.abs(deltaX) < Math.abs(deltaY) * 2.5) return;

    if (deltaX > 0 && !editSektionRender) setNavOffen(true);
    else if (deltaX < 0 && navOffen) setNavOffen(false);
  };

  // ── rohtext helper (for save) ─────────────────────────
  function parseCvRohtext(rohtext) {
    const teile = rohtext.replace(/\s*---\s*$/, '').trim().split(/\n?## /);
    const sektionen = teile.slice(1).map((teil) => {
      const zeilen = teil.split('\n');
      const name = zeilen[0].trim();
      const inhaltRaw = zeilen.slice(1).join('\n').trim();
      return { name, inhaltRaw };
    });
    return { sektionen };
  }

  const dropdownItemStyle = {
    display: 'flex', alignItems: 'center', gap: 10,
    width: '100%', padding: '10px 14px',
    borderRadius: 10, background: 'none', border: 'none',
    cursor: 'pointer', fontSize: 14, fontWeight: 500,
    fontFamily: "'Geist', -apple-system, sans-serif",
    color: '#1C1812', textAlign: 'left',
    transition: 'background 0.12s',
    WebkitTapHighlightColor: 'transparent',
  };


  // ── Render: flow mode ─────────────────────────────────

  if (ansicht === 'flow' && aktiveStelle) {
    return (
      <div
        style={{ position: 'relative', width: '100%', height: '100dvh', overflow: 'hidden' }}
        onTouchStart={onSwipeTouchStart}
        onTouchMove={onSwipeTouchMove}
        onTouchEnd={onFlowTouchEnd}
      >
        {/* NavIsland overlay — slides in from the left */}
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          transform: navOffen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
          zIndex: 50, willChange: 'transform',
        }}>
          <NavIsland
            nutzer={nutzer}
            stellen={stellen}
            activeNav={activeNav}
            onNavSelect={(id) => {
              onNavSelect(id);
              setAnsicht('main');
              setAutoStart(false);
            }}
            onProfilOeffnen={onProfilOeffnen}
            markierteIds={markierteIds}
            onMarkierenToggle={onMarkierenToggle}
            onStelleAuswaehlen={(s) => {
              onAuswahl(s);
              setAutoStart(false);
              setNavOffen(false);
            }}
            aktiveStelleId={aktiveStelle.id}
            th={th}
            onThemeChange={onThemeChange}
            onNeu={() => { setNavOffen(false); setTimeout(() => oeffneModal(), 300); }}
            onDelete={onStelleLoeschen}
            onRename={onStelleUmbenennen}
            onFeedback={() => { setNavOffen(false); setTimeout(() => setFeedbackModalOffen(true), 300); }}
            rolle={rolle}
            onAbmelden={onAbmelden}
          />
        </div>

        {/* Flow content wrapper — slides right when nav opens */}
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          transform: navOffen ? 'translateX(100%)' : 'translateX(0)',
          transition: 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
          willChange: 'transform', overflow: 'hidden',
        }}>
          <MobileStelleFlow
            stelle={aktiveStelle}
            tonlage={tonlage}
            onZurueck={() => { setAnsicht('main'); setAutoStart(false); setNavOffen(false); setEditSektion(null); setEditSektionRender(null); }}
            onGeaendert={onGeaendert}
            nutzer={nutzer}
            onProfilOeffnen={onProfilOeffnen}
            autoStart={autoStart}
            onSektionBearbeiten={onSektionBearbeiten}
            aktivesProfile={aktivesProfile}
            onKeinKontingent={onKeinKontingent}
            sheetIsOpen={!!editSektion}
          />

          {/* Section editor — MobileBottomSheet */}
          <MobileBottomSheet
            isOpen={!!editSektion}
            onClose={onSektionEditorSchliessen}
          >
            {editSektionRender && (
              <MobileSektionEditor
                sektion={editSektionRender}
                onZurueck={onSektionEditorSchliessen}
                onSave={onSektionSave}
              />
            )}
          </MobileBottomSheet>
        </div>

      </div>
    );
  }

  return (
    <div
      style={{ position: 'relative', width: '100%', height: '100dvh', overflow: 'hidden' }}
      onTouchStart={onSwipeTouchStart}
      onTouchMove={onSwipeTouchMove}
      onTouchEnd={onMainTouchEnd}
    >
      {/* NavIsland — slides in from left, same plane as content */}
      <div style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        transform: navOffen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
        willChange: 'transform',
      }}>
        <NavIsland
          nutzer={nutzer}
          stellen={stellen}
          activeNav={activeNav}
          onNavSelect={onNavSelect}
          markierteIds={markierteIds}
          onMarkierenToggle={onMarkierenToggle}
          onStelleAuswaehlen={onStelleVomNav}
          th={th}
          onNeu={() => { setNavOffen(false); setTimeout(() => oeffneModal(), 300); }}
          onDelete={onStelleLoeschen}
          onRename={onStelleUmbenennen}
          rolle={rolle}
          onAbmelden={onAbmelden}
        />
      </div>

      {/* Main content — slides right when nav opens */}
      <div style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        transform: navOffen ? 'translateX(100%)' : 'translateX(0)',
        transition: 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
        willChange: 'transform', overflow: 'hidden',
      }}>
        {activeNav === 'einstellungen' ? (
          <MobileEinstellungen
            nutzer={nutzer}
            onZurueck={() => setLocalView(null)}
            onAbmelden={onAbmelden}
            themeId={themeId}
            onThemeChange={onThemeChange}
            tonlage={tonlage}
            onTonlageChange={(t) => { setTonlage(t); localStorage.setItem('tonlage', t); }}
          />
        ) : activeNav === 'admin' && rolle === 'admin' ? (
          <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <AdminPanel nutzer={nutzer} />
          </div>
        ) : activeNav === 'analytics' ? (
          <div style={{ position: 'absolute', inset: 0 }}>
            <MobileAnalytics
              stellen={stellen}
              save={saveStelleField}
              markierteIds={markierteIds}
              onMarkierenToggle={onMarkierenToggle}
              onBearbeiten={onStelleBearbeiten}
              onExternErstellen={(stelle) => setStellen(prev => [...prev, stelle])}
            />
          </div>
        ) : (activeNav === 'profil') ? (
          <MobileProfil
            nutzer={nutzer}
            aktivesProfile={aktivesProfile}
            stellen={stellen}
            onStelleOeffnen={(s) => {
              if (s === 'neu') { oeffneModal(); return; }
              onAuswahl(s); setAutoStart(false); setAnsicht('flow');
            }}
          />
        ) : activeNav === 'dokumente' ? (
          <MobileMeineDokumente
            nutzer={nutzer}
            stellen={stellen}
            aktivesProfile={aktivesProfile}
            onStelleOeffnen={(s) => { navigate('/dashboard'); onAuswahl(s); setAutoStart(false); setAnsicht('flow'); }}
          />
        ) : activeNav === 'suche' ? (
          <MobileSucheOverlay
            stellen={stellen}
            onSchliessen={() => navigate('/dashboard')}
            onStelleAuswaehlen={(s) => { navigate('/dashboard'); onAuswahl(s); setAutoStart(false); }}
            markierteIds={markierteIds}
            onMarkierenToggle={onMarkierenToggle}
            onDelete={onStelleLoeschen}
            onRename={onStelleUmbenennen}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: T.bgGrad, color: T.text,
            display: 'flex', flexDirection: 'column',
            fontFamily: "'Geist', -apple-system, system-ui, sans-serif",
            WebkitFontSmoothing: 'antialiased',
          }}>
            <div className="ms-scroll">
              <div style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)' }} />

              {/* Top bar: Dashboard left (opens drawer), Avatar right (opens profile) */}
              <div style={{ padding: '0 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button
                  onClick={() => setNavOffen(true)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: 0, WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <span style={{
                    fontSize: 24, fontWeight: 800, letterSpacing: -0.5,
                    color: T.text, fontFamily: "'Geist', -apple-system, sans-serif",
                  }}>Dashboard</span>
                </button>
                <div ref={profilMenuRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setProfilMenuOffen(v => !v)}
                    style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: '#004D40', color: '#FFBC00',
                      border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
                      boxShadow: profilMenuOffen
                        ? '0 2px 10px rgba(0,77,64,0.28), 0 0 0 2px rgba(255,188,0,0.45)'
                        : '0 2px 10px rgba(0,77,64,0.28)',
                      WebkitTapHighlightColor: 'transparent',
                      overflow: 'hidden', flexShrink: 0,
                      transition: 'box-shadow 0.15s',
                    }}
                  >
                    {nutzer?.photoURL
                      ? <img src={nutzer.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : initialen
                    }
                  </button>

                  {profilMenuOffen && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 10px)',
                      right: 0,
                      background: '#FEFCF6',
                      border: '1px solid rgba(27,23,15,0.09)',
                      borderRadius: 16,
                      boxShadow: '0 16px 40px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.08)',
                      minWidth: 210,
                      padding: 6,
                      zIndex: 200,
                      animation: 'dropdownIn 0.15s ease',
                    }}>
                      {/* Nutzer-Info */}
                      <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid rgba(27,23,15,0.07)', marginBottom: 4 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1C1812', letterSpacing: -0.2 }}>
                          {nutzer?.displayName || 'Nutzer'}
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(28,24,18,0.45)', marginTop: 2 }}>
                          {nutzer?.email || ''}
                        </div>
                      </div>

                      {/* Feedback */}
                      <button
                        onClick={() => { setProfilMenuOffen(false); setFeedbackModalOffen(true); }}
                        style={dropdownItemStyle}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,77,64,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ flexShrink: 0 }}>
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 8v4M12 16h.01" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                        Feedback
                      </button>

                      {/* Nachtmodus toggle */}
                      <button
                        onClick={() => toggleDarkMode?.()}
                        style={{ ...dropdownItemStyle, justifyContent: 'space-between' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,77,64,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ flexShrink: 0 }}>
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Nachtmodus
                        </div>
                        <div style={{
                          width: 34, height: 20, borderRadius: 10,
                          background: darkMode ? '#004D40' : 'rgba(0,0,0,0.12)',
                          position: 'relative', flexShrink: 0,
                          transition: 'background 0.2s',
                        }}>
                          <div style={{
                            position: 'absolute', top: 2,
                            left: darkMode ? 16 : 2,
                            width: 16, height: 16, borderRadius: '50%',
                            background: '#fff',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                          }} />
                        </div>
                      </button>

                      {/* Profil */}
                      <button
                        onClick={() => { setProfilMenuOffen(false); onProfilOeffnen(); }}
                        style={dropdownItemStyle}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,77,64,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ flexShrink: 0 }}>
                          <circle cx="12" cy="8" r="4" strokeWidth="1.6"/>
                          <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                        Profil
                      </button>

                      {/* Einstellungen */}
                      <button
                        onClick={() => { setProfilMenuOffen(false); setLocalView('einstellungen'); }}
                        style={dropdownItemStyle}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,77,64,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                          <circle cx="12" cy="12" r="3" stroke="#004D40" strokeWidth="1.6"/>
                          <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
                            stroke="#004D40" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                        Einstellungen
                      </button>

                      {/* Divider */}
                      <div style={{ height: 1, background: 'rgba(27,23,15,0.08)', margin: '4px 8px' }} />

                      {/* Abmelden */}
                      <button
                        onClick={() => { setProfilMenuOffen(false); onAbmelden(); }}
                        style={{ ...dropdownItemStyle, color: '#dc2626' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,38,38,0.07)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke="#dc2626" strokeWidth="1.6" strokeLinecap="round"/>
                          <polyline points="10 17 15 12 10 7" stroke="#dc2626" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="15" y1="12" x2="3" y2="12" stroke="#dc2626" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                        Abmelden
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Teal Hero */}
              <BewerbungsrateCard stellen={stellen} />

              {/* KPI Row */}
              <KPIRow stellen={stellen} />

              {/* Profil Card */}
              <div style={{ marginTop: 20 }}>
                <ProfilVollstaendigkeitKarte
                  profile={aktivesProfile}
                  hatProfile={hatProfile}
                  profilLaedt={profilLaedt}
                  onClick={onBasisBearbeiten}
                />
              </div>

              {(() => {
                const nichtAbgeschickt = stellen.filter(s =>
                  !['abgeschickt', 'absage', 'kennenlerngespraech', 'zusage'].includes(s.bewerbungs_status)
                );
                const vorname = aktivesProfile?.vorname
                  || nutzer?.displayName?.split(' ')[0]
                  || 'du';
                const anzahl = nichtAbgeschickt.length;
                return (
                  <>
                    {/* Greeting */}
                    <div style={{ padding: '28px 20px 4px' }}>
                      <div style={{
                        fontSize: 30, lineHeight: 1.1,
                        fontFamily: "'DM Serif Display', Georgia, serif",
                        fontStyle: 'italic',
                        color: T.text, letterSpacing: -0.5,
                      }}>
                        Hi {vorname} —
                      </div>
                      <div style={{ fontSize: 14, color: T.textMuted, marginTop: 6, lineHeight: 1.5 }}>
                        {anzahl === 0
                          ? 'Alle Bewerbungen sind abgeschickt.'
                          : `${anzahl} Bewerbung${anzahl !== 1 ? 'en warten' : ' wartet'} auf dich.`}
                      </div>
                    </div>

                    {/* Section header */}
                    {anzahl > 0 && (
                      <div style={{ padding: '22px 20px 14px' }}>
                        <div style={{ fontSize: 9, fontFamily: T.mono, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.textDim, fontWeight: 700, marginBottom: 6 }}>Heute</div>
                        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.6, color: T.text, lineHeight: 1 }}>Abschicken</div>
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px' }}>
                      {nichtAbgeschickt.slice(0, 4).map(s => (
                        <DashboardJobCard key={s.id} stelle={s} onOpen={() => onStelleAuswahl(s)} onDelete={onStelleLoeschen} onAbschicken={onStelleAbschicken} markiert={markierteIds.has(s.id)} onMarkierenToggle={onMarkierenToggle} onRename={onStelleUmbenennen} />
                      ))}
                    </div>
                  </>
                );
              })()}

              <div style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 100px)' }} />
            </div>
          </div>
        )}
      </div>

      {/* Neue-Stelle-Insel — only on dashboard */}
      {activeNav === 'dashboard' && (
        <div style={{
          position: 'fixed',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)',
          right: 20,
          zIndex: 40,
        }}>
          <button
            onClick={oeffneModal}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '13px 24px',
              background: '#FFBC00',
              border: 'none', borderRadius: 999,
              cursor: 'pointer', fontFamily: 'inherit',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 8px 24px rgba(255,188,0,0.45), 0 2px 8px rgba(0,0,0,0.12)',
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{
              width: 26, height: 26, borderRadius: 999,
              background: '#004D40',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="14" height="14" viewBox="0 0 14 14">
                <path d="M7 2v10M2 7h10" stroke="#FFBC00" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#004D40', letterSpacing: '0.01em' }}>
              Neue Stelle
            </span>
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 60, background: T.modalBg, color: T.text,
          padding: '11px 16px', borderRadius: 999,
          border: `1px solid ${T.border}`,
          fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 8px 24px rgba(28,24,18,0.1)',
          whiteSpace: 'nowrap',
          animation: 'msToastIn 0.22s ease',
        }}>
          <div style={{
            width: 18, height: 18, borderRadius: 999,
            background: T.accent, color: T.accentText,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckIcon />
          </div>
          {typeof toast === 'object' ? toast.msg : toast}
        </div>
      )}

      {/* Modal */}
      {modal && <AddJobModal onClose={() => setModal(null)} onAdd={onNeu} laedt={laedt || pdfLaedt} initialTab={modal.tab} initialPrefill={modal.prefill} hatProfile={hatProfile && !profilLaedt} onProfilErstellen={() => { setModal(null); onBasisBearbeiten(); }} />}

      {/* Kein-Profil Warnung */}
      {keinProfilWarnung && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 20px',
          }}
          onClick={() => setKeinProfilWarnung(false)}
        >
          <div
            style={{
              background: T.bg, borderRadius: 24, padding: '36px 28px',
              width: '100%', maxWidth: 380, textAlign: 'center',
              boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 44, marginBottom: 14 }}>👤</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 10, letterSpacing: -0.4 }}>
              Noch kein Profil vorhanden
            </div>
            <div style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6, marginBottom: 28 }}>
              Damit dein Anschreiben personalisiert werden kann, musst du zuerst ein Profil mit deinen Daten anlegen.
            </div>
            <button
              style={{
                width: '100%', padding: '14px', marginBottom: 10,
                background: T.accent, color: T.accentText,
                border: 'none', borderRadius: 30, fontSize: 15, fontWeight: 700,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                boxShadow: `0 4px 16px ${T.accent}44`,
              }}
              onClick={() => { setKeinProfilWarnung(false); onProfilOeffnen(); }}
            >
              Profil erstellen
            </button>
            <button
              style={{
                width: '100%', padding: '12px',
                background: 'none', border: `1.5px solid ${T.border}`,
                borderRadius: 30, fontSize: 14, color: T.textMuted,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              }}
              onClick={() => setKeinProfilWarnung(false)}
            >
              Später
            </button>
          </div>
        </div>
      )}

      {/* Duplikat-Warnung */}
      {duplikatWarnung && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.48)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
          onClick={() => setDuplikatWarnung(null)}
        >
          <div
            style={{
              background: T.bg,
              borderRadius: '24px 24px 0 0',
              padding: '28px 24px calc(env(safe-area-inset-bottom,0px) + 28px)',
              width: '100%', maxWidth: 480,
              boxShadow: '0 -8px 48px rgba(0,0,0,0.18)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle */}
            <div style={{ width: 36, height: 4, borderRadius: 999, background: T.border, margin: '0 auto 22px' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 14, flexShrink: 0,
                background: 'rgba(200,100,40,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M11 8v4M11 15h.01" stroke="#C86428" strokeWidth="1.8" strokeLinecap="round"/>
                  <circle cx="11" cy="11" r="9" stroke="#C86428" strokeWidth="1.6"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.text, letterSpacing: -0.3 }}>Bereits vorhanden</div>
                <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>
                  Diese Stellenanzeige wurde schon einmal hinzugefügt.
                </div>
              </div>
            </div>

            <div style={{
              background: 'rgba(200,100,40,0.07)',
              border: '1px solid rgba(200,100,40,0.18)',
              borderRadius: 14, padding: '12px 14px',
              fontSize: 13, color: T.textMuted, lineHeight: 1.55, marginBottom: 24,
            }}>
              Möchtest du sie trotzdem nochmal hinzufügen? Das erstellt eine zweite, separate Bewerbung für die gleiche Stelle.
            </div>

            <button
              style={{
                width: '100%', padding: '15px', marginBottom: 10,
                background: '#C86428', color: '#FBF7ED',
                border: 'none', borderRadius: 30, fontSize: 15, fontWeight: 700,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                boxShadow: '0 4px 16px rgba(200,100,40,0.3)',
              }}
              onClick={() => erstelleStelleWirklich(duplikatWarnung)}
            >
              Trotzdem hinzufügen
            </button>
            <button
              style={{
                width: '100%', padding: '13px',
                background: 'none', border: `1.5px solid ${T.border}`,
                borderRadius: 30, fontSize: 14, color: T.textMuted,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              }}
              onClick={() => setDuplikatWarnung(null)}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {feedbackModalOffen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(28,24,18,0.45)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            padding: '0 12px',
          }}
          onClick={() => { setFeedbackModalOffen(false); setFeedbackGesendet(false); setFeedbackBetreff(''); setFeedbackNachricht(''); setFeedbackBilder([]); setFeedbackFehler(''); }}
        >
          <div
            style={{
              background: 'linear-gradient(160deg, #FEFCF6 0%, #F5F0E4 100%)',
              borderRadius: 28,
              padding: '28px 24px 24px',
              width: '100%', maxWidth: 480, boxSizing: 'border-box', position: 'relative',
              marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              boxShadow: '0 32px 80px rgba(28,24,18,0.35), 0 8px 24px rgba(28,24,18,0.12)',
              maxHeight: 'calc(100dvh - 80px)', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
              animation: 'msBlup 0.42s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => { setFeedbackModalOffen(false); setFeedbackGesendet(false); setFeedbackBetreff(''); setFeedbackNachricht(''); setFeedbackBilder([]); setFeedbackFehler(''); }}
              style={{
                position: 'absolute', top: 18, right: 18,
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(0,77,64,0.07)', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent', color: '#5B6964',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>

            {feedbackGesendet ? (
              <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
                {/* Success ring */}
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: 'rgba(0,77,64,0.08)', border: '2px solid rgba(0,77,64,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12l5 5L19 7" stroke="#004D40" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h2 style={{
                  margin: '0 0 8px', fontFamily: "'DM Serif Display', Georgia, serif",
                  fontSize: 28, fontWeight: 400, color: '#0F1E1A', letterSpacing: -0.5,
                }}>
                  Danke für dein <em style={{ fontStyle: 'italic', color: '#FFBC00' }}>Feedback!</em>
                </h2>
                <p style={{ fontSize: 14, color: '#5B6964', marginBottom: 32, lineHeight: 1.6 }}>
                  Wir melden uns so schnell wie möglich bei dir.
                </p>
                <button
                  style={{
                    width: '100%', padding: '15px',
                    background: '#004D40', color: '#FFBC00',
                    border: 'none', borderRadius: 9999, fontSize: 15, fontWeight: 700,
                    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 4px 16px rgba(0,77,64,0.25)',
                    letterSpacing: 0.1,
                  }}
                  onClick={() => { setFeedbackModalOffen(false); setFeedbackGesendet(false); }}
                >
                  Schließen
                </button>
              </div>
            ) : (
              <>
                {/* Eyebrow */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#FFBC00', flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#004D40' }}>
                    Feedback
                  </span>
                </div>

                {/* Title */}
                <h2 style={{
                  margin: '0 0 6px', fontFamily: "'DM Serif Display', Georgia, serif",
                  fontSize: 28, fontWeight: 400, lineHeight: 1.1, color: '#0F1E1A', letterSpacing: -0.5,
                }}>
                  Feature wünschen /<br /><em style={{ fontStyle: 'italic', color: '#FFBC00' }}>Bug melden.</em>
                </h2>
                <p style={{ fontSize: 13, color: '#5B6964', marginBottom: 24, lineHeight: 1.55 }}>
                  Teile uns mit, was du dir wünschst oder was nicht funktioniert.
                </p>

                {/* Betreff */}
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#004D40', marginBottom: 7 }}>Betreff</div>
                <input
                  placeholder="z. B. Export als PDF funktioniert nicht"
                  value={feedbackBetreff}
                  onChange={e => setFeedbackBetreff(e.target.value)}
                  maxLength={120}
                  style={{
                    width: '100%', padding: '13px 16px', boxSizing: 'border-box',
                    border: '1.5px solid rgba(0,0,0,0.10)', borderRadius: 10,
                    background: '#FFFFFF', fontFamily: 'inherit',
                    fontSize: 14, color: '#0F1E1A', outline: 'none', marginBottom: 16,
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = 'rgba(255,188,0,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,188,0,0.14)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(0,0,0,0.10)'; e.target.style.boxShadow = 'none'; }}
                />

                {/* Nachricht */}
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#004D40', marginBottom: 7 }}>Nachricht</div>
                <textarea
                  placeholder="Beschreibe den Bug oder dein Wunsch-Feature so genau wie möglich…"
                  value={feedbackNachricht}
                  onChange={e => setFeedbackNachricht(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  style={{
                    width: '100%', padding: '13px 16px', boxSizing: 'border-box',
                    border: '1.5px solid rgba(0,0,0,0.10)', borderRadius: 10,
                    background: '#FFFFFF', fontFamily: 'inherit',
                    fontSize: 14, color: '#0F1E1A', outline: 'none',
                    resize: 'none', marginBottom: 16,
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = 'rgba(255,188,0,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,188,0,0.14)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(0,0,0,0.10)'; e.target.style.boxShadow = 'none'; }}
                />

                {/* Screenshots */}
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#004D40', marginBottom: 7 }}>
                  Screenshots <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: 11, fontWeight: 400, color: '#8B9590' }}>(optional, max. 3 × 5 MB)</span>
                </div>
                {feedbackBilder.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    {feedbackBilder.map((b, i) => (
                      <div key={i} style={{ position: 'relative', width: 68, height: 68 }}>
                        <img src={b.dataUrl} alt={b.dateiname} style={{ width: 68, height: 68, objectFit: 'cover', borderRadius: 10, border: '1.5px solid rgba(0,0,0,0.08)' }} />
                        <button
                          onClick={() => setFeedbackBilder(prev => prev.filter((_, j) => j !== i))}
                          style={{
                            position: 'absolute', top: -6, right: -6,
                            width: 22, height: 22, borderRadius: 999,
                            background: '#dc2626', color: '#fff',
                            border: '2px solid #FEFCF6', cursor: 'pointer', fontSize: 10,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700,
                          }}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {feedbackBilder.length < 3 && (
                  <label style={{
                    position: 'relative', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    width: '100%', padding: '13px', boxSizing: 'border-box',
                    background: 'rgba(0,77,64,0.04)', border: '1.5px dashed rgba(0,77,64,0.22)',
                    borderRadius: 10, fontSize: 13, fontWeight: 500, color: '#5B6964',
                    cursor: 'pointer', marginBottom: 14,
                    WebkitTapHighlightColor: 'transparent',
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#004D40" strokeWidth="1.8" strokeLinecap="round">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                    Bild hinzufügen
                    <input
                      type="file" accept="image/*" multiple
                      onChange={e => {
                        setFeedbackFehler('');
                        const files = Array.from(e.target.files || []);
                        if (feedbackBilder.length + files.length > 3) { setFeedbackFehler('Maximal 3 Bilder erlaubt.'); e.target.value = ''; return; }
                        const tooBig = files.find(f => f.size > 5 * 1024 * 1024);
                        if (tooBig) { setFeedbackFehler(`"${tooBig.name}" ist größer als 5 MB.`); e.target.value = ''; return; }
                        Promise.all(files.map(f => new Promise((res, rej) => {
                          const r = new FileReader();
                          r.onload = () => res({ dateiname: f.name, dataUrl: r.result });
                          r.onerror = rej;
                          r.readAsDataURL(f);
                        }))).then(neu => setFeedbackBilder(prev => [...prev, ...neu]));
                        e.target.value = '';
                      }}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                    />
                  </label>
                )}

                {feedbackFehler && (
                  <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                    {feedbackFehler}
                  </div>
                )}

                {/* Submit */}
                <button
                  disabled={feedbackLaedt || !feedbackBetreff.trim() || !feedbackNachricht.trim()}
                  onClick={async () => {
                    setFeedbackLaedt(true); setFeedbackFehler('');
                    try {
                      const anhaenge = feedbackBilder.map(b => ({ dateiname: b.dateiname, inhalt_base64: b.dataUrl.split(',')[1] }));
                      await feedbackSenden(feedbackBetreff.trim(), feedbackNachricht.trim(), anhaenge);
                      setFeedbackGesendet(true); setFeedbackBetreff(''); setFeedbackNachricht(''); setFeedbackBilder([]);
                    } catch { setFeedbackFehler('Fehler beim Senden. Bitte nochmal versuchen.'); }
                    finally { setFeedbackLaedt(false); }
                  }}
                  style={{
                    width: '100%', padding: '15px',
                    background: (feedbackLaedt || !feedbackBetreff.trim() || !feedbackNachricht.trim()) ? '#F0EAD8' : '#FFBC00',
                    color: (feedbackLaedt || !feedbackBetreff.trim() || !feedbackNachricht.trim()) ? '#8B9590' : '#002B25',
                    border: 'none', borderRadius: 9999, fontSize: 15, fontWeight: 700,
                    cursor: (feedbackLaedt || !feedbackBetreff.trim() || !feedbackNachricht.trim()) ? 'default' : 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: (feedbackLaedt || !feedbackBetreff.trim() || !feedbackNachricht.trim()) ? 'none' : '0 4px 18px rgba(255,188,0,0.35)',
                    transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
                    letterSpacing: 0.1, marginBottom: 4,
                  }}
                >
                  {feedbackLaedt ? 'Wird gesendet…' : 'Absenden →'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default MobileStudio;
